/**
 * Libellés FR des champs cités par un `422`.
 *
 * Module PUR → testable sans mock.
 *
 * ## Pourquoi ce fichier existe
 * Le backend renvoie, pour chaque champ en faute, un `message` **en anglais** :
 * ce sont ceux de Laravel, non traduits.
 *
 *     { "field": "delivery_address",
 *       "message": "The delivery address field is required when trip type is LIVRAISON." }
 *
 * Le contrat du 2026-08-26 est explicite : ne pas les afficher tels quels à un
 * utilisateur francophone. Jusqu'ici l'app les concaténait et les montrait
 * dans une boîte de dialogue — le client lisait donc une phrase anglaise citant
 * un chemin de champ JSON pour apprendre qu'il manquait un numéro de téléphone.
 *
 * Le `field`, lui, « porte le chemin exact et stable » : c'est LUI la clé de nos
 * propres libellés. On perd la nuance du message serveur (obligatoire ? mal
 * formé ?) et on l'assume : savoir QUEL champ reprendre vaut mieux qu'une
 * phrase exacte mais illisible.
 */

/**
 * Chemins normalisés → libellé FR.
 *
 * Les indices sont remplacés par `{i}` avant la recherche : `stops.0.location`
 * et `stops.7.location` partagent donc la même entrée, et le numéro d'arrêt est
 * réinjecté ensuite (cf. `fieldLabel`).
 */
const LABELS: Record<string, string> = {
  // ── Livraison : les cinq champs devenus obligatoires le 2026-08-26 ────────
  "delivery_address":               "Adresse de livraison",
  "delivery_address.contact_name":  "Nom du destinataire",
  "delivery_address.contact_phone": "Téléphone du destinataire",
  "delivery_address.line1":         "Adresse de livraison",
  "delivery_address.city":          "Ville de livraison",
  "delivery_address.latitude":      "Position du point de livraison",
  "delivery_address.longitude":     "Position du point de livraison",
  // Le contrat mélange les deux formes : `DeliveryAddress` déclare
  // `latitude`/`longitude`, `StopLocation` et l'adresse d'un article veulent
  // `lat`/`lng`. Les quatre mènent au même endroit pour l'utilisateur.
  "delivery_address.lat":           "Position du point de livraison",
  "delivery_address.lng":           "Position du point de livraison",

  // ── Arrêts ────────────────────────────────────────────────────────────────
  "stops":                             "Arrêts de la course",
  "stops.{i}":                         "Arrêt {n}",
  "stops.{i}.location":                "Adresse de l'arrêt {n}",
  "stops.{i}.location.lat":            "Position de l'arrêt {n}",
  "stops.{i}.location.lng":            "Position de l'arrêt {n}",
  "stops.{i}.location.address":        "Adresse de l'arrêt {n}",
  "stops.{i}.location.contact_name":   "Nom du contact au retrait (arrêt {n})",
  "stops.{i}.location.contact_phone":  "Téléphone du contact au retrait (arrêt {n})",
  "stops.{i}.stop_index":              "Ordre de l'arrêt {n}",
  "stops.{i}.items":                   "Articles de l'arrêt {n}",
  "stops.{i}.items.{i}.name":          "Nom d'un article (arrêt {n})",
  "stops.{i}.items.{i}.quantity":      "Quantité d'un article (arrêt {n})",
  "stops.{i}.items.{i}.estimated_price": "Prix estimé d'un article (arrêt {n})",
  "stops.{i}.items.{i}.delivery_address": "Adresse de livraison d'un article (arrêt {n})",
  "stops.{i}.items.{i}.delivery_address.lat": "Position de livraison d'un article (arrêt {n})",
  "stops.{i}.items.{i}.delivery_address.lng": "Position de livraison d'un article (arrêt {n})",
  // Chemins RACINE : certains validateurs citent la feuille seule, sans son
  // chemin. « Lat » tout court ne disait rien à personne.
  "lat":                            "Coordonnées d'un point de la course",
  "lng":                            "Coordonnées d'un point de la course",

  // ── Course ────────────────────────────────────────────────────────────────
  "trip_type":   "Type de course",
  "distance_km": "Distance de la course",
  "user_id":     "Compte client",
  "is_emergency": "Urgence",

  // ── Portefeuille / paiements ──────────────────────────────────────────────
  "amount":         "Montant",
  "phone_number":   "Numéro de téléphone",
  "payment_method": "Moyen de paiement",
  "provider":       "Opérateur",

  // ── Preuve de retrait / média ─────────────────────────────────────────────
  "photo_media_id": "Photo",
  "note":           "Note",
  "reason":         "Motif",
  "file":           "Fichier",
  "type":           "Type de fichier",
  "order_id":       "Commande",
};

/** Indices numériques → `{i}`, pour que la table reste courte et lisible. */
function normalizePath(path: string): string {
  return path.replace(/\.\d+(?=\.|$)/g, ".{i}");
}

/**
 * Numéro d'arrêt HUMAIN (1-based) extrait d'un chemin `stops.N.…`.
 *
 * Le backend indexe à partir de 0 ; l'écran numérote les arrêts à partir de 1.
 * Renvoyer « arrêt 0 » enverrait le client chercher une carte qui n'existe pas.
 */
function stopNumber(path: string): number | null {
  const m = /^stops\.(\d+)(?:\.|$)/.exec(path);
  return m ? Number(m[1]) + 1 : null;
}

/**
 * Dernier recours quand le chemin n'est pas dans la table.
 *
 * Un champ inconnu ne doit ni faire disparaître l'erreur, ni afficher du
 * `snake_case` : on rend le segment final lisible. C'est imparfait, mais un
 * champ ajouté côté backend restera compréhensible sans redéploiement.
 */
function humanizeSegment(path: string): string {
  const last = path.split(".").filter((s) => !/^\d+$/.test(s)).pop() ?? path;
  const words = last.replace(/_/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Libellé FR d'un chemin de champ backend. */
export function fieldLabel(path: string | undefined | null): string | undefined {
  if (!path) return undefined;
  const clean = path.trim();
  if (!clean) return undefined;

  const template = LABELS[normalizePath(clean)];
  if (!template) return humanizeSegment(clean);

  const n = stopNumber(clean);
  return template.replace("{n}", n != null ? String(n) : "");
}

/**
 * Message affichable construit à partir des champs en faute d'un `422`.
 *
 * Rend `undefined` si aucun champ exploitable : l'appelant retombe alors sur le
 * message générique. On dédoublonne — le backend cite parfois deux fois le même
 * point (`delivery_address` puis `delivery_address.line1`), et deux puces
 * identiques donnent l'impression que l'app bafouille.
 */
export function describeFieldErrors(
  fields: { field?: string; message?: string }[] | undefined | null,
): string | undefined {
  if (!Array.isArray(fields) || fields.length === 0) return undefined;

  const labels: string[] = [];
  for (const f of fields) {
    const label = fieldLabel(f?.field);
    if (label && !labels.includes(label)) labels.push(label);
  }
  if (labels.length === 0) return undefined;

  if (labels.length === 1) {
    return `${labels[0]} : information manquante ou invalide.`;
  }
  return `Ces informations sont manquantes ou invalides :\n${labels
    .map((l) => `• ${l}`)
    .join("\n")}`;
}
